import styled, { css } from 'styled-components'

import { theme } from '../../constants'

export type CardVariant = 'default' | 'highlight' | 'interactive'
export type CardAccent = 'blue' | 'yellow' | 'coral' | 'none'

interface CardContainerProps {
  $variant: CardVariant
  $accent: CardAccent
  $padding: 'small' | 'medium' | 'large'
}

const accentColors = {
  blue: theme.colors.brandBlue,
  yellow: theme.colors.brandYellow,
  coral: theme.colors.brandCoral,
  none: 'transparent',
}

const paddingSizes = {
  small: theme.spacing[4],
  medium: theme.spacing[6],
  large: theme.spacing[8],
}

export const CardContainer = styled.article<CardContainerProps>`
  position: relative;
  padding: ${({ $padding }) => paddingSizes[$padding]};
  overflow: hidden;
  color: ${theme.semanticColors.text.primary};
  background: ${({ $variant, $accent }) =>
    $variant === 'highlight' && $accent === 'blue'
      ? theme.colors.brandBlueLight
      : $variant === 'highlight' && $accent === 'yellow'
        ? 'var(--redesign-student-soft, #FFF1B3)'
        : $variant === 'highlight' && $accent === 'coral'
          ? theme.colors.brandCoralLight
          : theme.colors.surface};
  border: 1px solid ${theme.semanticColors.border.default};
  border-radius: ${({ $variant }) =>
    $variant === 'highlight' ? theme.radii.xlarge : theme.radii.large};
  box-shadow: ${theme.shadows.small};

  &::before {
    content: '';
    position: absolute;
    inset: 0 auto 0 0;
    width: ${({ $accent }) => ($accent === 'none' ? '0' : '4px')};
    background: ${({ $accent }) => accentColors[$accent]};
  }

  ${({ $variant }) =>
    $variant === 'interactive' &&
    css`
      cursor: pointer;
      transition:
        transform ${theme.motion.normal} ${theme.motion.easing},
        box-shadow ${theme.motion.normal} ${theme.motion.easing};

      &:hover {
        transform: translateY(-3px);
        box-shadow: ${theme.shadows.medium};
      }

      &:focus-within {
        box-shadow: ${theme.shadows.focus};
      }
    `}

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: ${({ $padding }) =>
      $padding === 'large' ? theme.spacing[6] : paddingSizes[$padding]};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`
